import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Lock, Check, AlertTriangle, LogOut } from "lucide-react";
import BrandMark from "@/components/app/BrandMark";

const PERKS = [
  "1 manager account",
  "Up to 10 active employees",
  "Unlimited tools & exposures",
  "PDF + CSV compliance reports",
  "Hand-health check-ins",
  "GDPR account deletion built in",
];

export default function Locked({ status, onDelete }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const plan = status?.plan;
  const company = status?.company;

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const subject = encodeURIComponent(`HAVS Guard NT subscription — ${company?.name || ""}`);
  const body = encodeURIComponent(
    `Hi,\n\nI'd like to subscribe ${company?.name || "my site"} to HAVS Guard NT (£${plan?.price_gbp ?? 99} / year).\n\nThanks.`
  );

  return (
    <div className="min-h-screen bg-slate-50 flex items-start sm:items-center justify-center p-6">
      <div className="w-full max-w-3xl">
        <div className="flex items-center justify-between mb-8">
          <BrandMark size="md" />
          <Button
            variant="ghost"
            size="sm"
            onClick={doLogout}
            data-testid="locked-logout-btn"
            className="gap-2 text-slate-600"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </div>

        <Card className="p-6 sm:p-10 border-amber-300 bg-amber-50/60" data-testid="locked-card">
          <div className="flex items-start gap-3 mb-6">
            <div className="h-10 w-10 rounded-md bg-amber-100 text-amber-700 flex items-center justify-center">
              <Lock className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-700">
                Trial ended
              </p>
              <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-slate-900 mt-1">
                {company?.name || "Your site"} is read-only.
              </h1>
              <p className="text-slate-700 mt-2">
                Your 7-day free trial has finished. Subscribe to keep recording
                exposures, or delete the account permanently below.
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-white border-2 border-slate-900 rounded-lg p-6">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                {plan?.name || "HAVS Guard NT — Annual"}
              </p>
              <p className="font-display text-4xl font-black tracking-tighter text-slate-900 mt-2">
                £{plan?.price_gbp ?? 99}
                <span className="text-base font-medium text-slate-500 ml-1">
                  / year
                </span>
              </p>
              <p className="text-sm text-slate-600 mt-1">
                1 manager · {plan?.max_workers ?? 10} workers
              </p>
              <ul className="mt-5 space-y-2 text-sm text-slate-700">
                {PERKS.map((p) => (
                  <li key={p} className="flex items-start gap-2">
                    <Check className="h-4 w-4 mt-0.5 text-emerald-600" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              <Button
                asChild
                className="w-full mt-6 h-11 bg-slate-900 hover:bg-slate-800"
                data-testid="locked-contact-sales-btn"
              >
                <a href={`mailto:sales@havsguard.com?subject=${subject}&body=${body}`}>
                  Contact sales to subscribe
                </a>
              </Button>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-6 flex flex-col">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                GDPR — Right to erasure
              </p>
              <h3 className="font-display text-xl font-bold text-slate-900 mt-2">
                Delete this account
              </h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                Permanently removes your company, all employees, tools, exposure
                records, check-ins and user logins. This cannot be undone.
              </p>
              <div className="mt-4 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 p-3 rounded">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>
                  Export your exposure records as PDF/CSV before deleting if
                  you need them for compliance.
                </span>
              </div>
              <Button
                variant="outline"
                onClick={onDelete}
                className="w-full mt-auto h-11 border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
                data-testid="locked-delete-btn"
              >
                Delete account permanently
              </Button>
            </div>
          </div>

          <p className="mt-6 text-xs text-slate-500 text-center">
            Need a longer trial? Email{" "}
            <a className="underline" href="mailto:sales@havsguard.com">
              sales@havsguard.com
            </a>
            .
          </p>
        </Card>
      </div>
    </div>
  );
}
